/**
 * estimated-hours-lines — the outline a Resource Plan's estimate is built from.
 *
 * A plan's estimate is a small tree of Estimated Hours Lines (max depth 3, root = depth 1).
 * Lines are reference only: they explain where the estimate came from, they do not drive
 * the staffing grid.
 *
 * PURE MODULE — no `@/db`, no React, no server-only imports. The API validates a
 * reorder with the same functions the UI uses to compute it, so a structure the outline
 * editor proposes is exactly the structure the server accepts.
 *
 * All arithmetic is integer centi-hours via `parseHoursCenti` / `formatHoursCenti` from
 * `resource-plan-math` — hours strings never go through a float.
 *
 * Tolerance of malformed input: a line whose `parentLineId` names a line that is not in
 * the list is treated as a root, and lines trapped in a cycle are unreachable from any
 * root (they get an `effective` of their own but never contribute to a total, and are
 * not emitted by `orderedTree`). `validateStructure` is the gate that keeps either from
 * being persisted.
 */

import { formatHoursCenti, type HoursCenti, parseHoursCenti } from "@/lib/resource-plan-math";

/** Root lines are depth 1; nothing may sit deeper than this. */
export const MAX_LINE_DEPTH = 3;

export type EstimatedHoursLineNode = {
  id: string;
  parentLineId: string | null;
  workstreamId: string | null;
  /** Decimal string with ≤2dp (`"12"`, `"12.5"`, `"12.50"`), or null. */
  hours: string | null;
  included: boolean;
  position: number;
};

export type LineStructureEntry = { id: string; parentLineId: string | null; position: number };

type StructureLine = Pick<EstimatedHoursLineNode, "id" | "parentLineId" | "position">;

// ── Shared structure helpers ─────────────────────────────────────────────────

function compareSiblings(a: StructureLine, b: StructureLine): number {
  if (a.position !== b.position) return a.position - b.position;
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;
  return 0;
}

/** The parent a line is actually filed under: a dangling parent id reads as a root. */
function effectiveParent(line: StructureLine, ids: ReadonlySet<string>): string | null {
  return line.parentLineId !== null && ids.has(line.parentLineId) ? line.parentLineId : null;
}

/** parent id (null = root group) → children, sorted by position then id. */
function groupChildren<T extends StructureLine>(lines: readonly T[]): Map<string | null, T[]> {
  const ids = new Set(lines.map((l) => l.id));
  const groups = new Map<string | null, T[]>();
  for (const line of lines) {
    const parent = effectiveParent(line, ids);
    const group = groups.get(parent);
    if (group === undefined) groups.set(parent, [line]);
    else group.push(line);
  }
  for (const group of groups.values()) group.sort(compareSiblings);
  return groups;
}

function sumCenti(values: ReadonlyArray<HoursCenti | null>): HoursCenti | null {
  let total: HoursCenti | null = null;
  for (const v of values) {
    if (v === null) continue;
    total = (total ?? 0) + v;
  }
  return total;
}

function formatOrNull(centi: HoursCenti | null): string | null {
  return centi === null ? null : formatHoursCenti(centi);
}

// ── Rollup ───────────────────────────────────────────────────────────────────

export type EstimatedHoursRollup = {
  /** Σ effective of INCLUDED roots; null if none of them carries a number. */
  planHours: string | null;
  /** Root workstream tag → Σ effective of that workstream's INCLUDED roots. */
  byWorkstream: Map<string, string | null>;
  /** Every line's effective hours — computed as if included, so excluded rows can show muted hours. */
  effective: Map<string, string | null>;
};

export function rollupEstimatedHours(
  lines: readonly EstimatedHoursLineNode[],
): EstimatedHoursRollup {
  const groups = groupChildren(lines);
  const memo = new Map<string, HoursCenti | null>();
  const inProgress = new Set<string>();

  const effectiveOf = (line: EstimatedHoursLineNode): HoursCenti | null => {
    const cached = memo.get(line.id);
    if (cached !== undefined) return cached;
    // Cycle guard: a line reached again while computing itself contributes nothing.
    if (inProgress.has(line.id)) return null;
    inProgress.add(line.id);
    const children = groups.get(line.id) ?? [];
    // A parent ignores its own hours. An excluded child adds nothing — and in particular
    // must not turn a parent whose included children are all null into a 0.
    const value =
      children.length > 0
        ? sumCenti(children.filter((child) => child.included).map(effectiveOf))
        : line.hours === null
          ? null
          : parseHoursCenti(line.hours);
    inProgress.delete(line.id);
    memo.set(line.id, value);
    return value;
  };

  const effective = new Map<string, string | null>();
  for (const line of lines) effective.set(line.id, formatOrNull(effectiveOf(line)));

  const roots = groups.get(null) ?? [];
  const planHours = sumCenti(roots.filter((r) => r.included).map((r) => effectiveOf(r)));

  const wsCenti = new Map<string, Array<HoursCenti | null>>();
  for (const root of roots) {
    if (root.workstreamId === null) continue;
    const bucket = wsCenti.get(root.workstreamId) ?? [];
    if (root.included) bucket.push(effectiveOf(root));
    wsCenti.set(root.workstreamId, bucket);
  }
  const byWorkstream = new Map<string, string | null>();
  for (const [wsId, values] of wsCenti) byWorkstream.set(wsId, formatOrNull(sumCenti(values)));

  return { planHours: formatOrNull(planHours), byWorkstream, effective };
}

// ── Display order ────────────────────────────────────────────────────────────

export type OrderedLine<T> = { line: T; depth: number; hasChildren: boolean };

/** Depth-first display order; siblings sorted by position then id. Root depth is 1. */
export function orderedTree<T extends StructureLine>(lines: readonly T[]): Array<OrderedLine<T>> {
  const groups = groupChildren(lines);
  const out: Array<OrderedLine<T>> = [];
  const visited = new Set<string>();
  const walk = (parent: string | null, depth: number): void => {
    for (const line of groups.get(parent) ?? []) {
      if (visited.has(line.id)) continue;
      visited.add(line.id);
      const children = groups.get(line.id) ?? [];
      out.push({ line, depth, hasChildren: children.length > 0 });
      walk(line.id, depth + 1);
    }
  };
  walk(null, 1);
  return out;
}

// ── Tree operations ──────────────────────────────────────────────────────────

/** Mutable outline: parent id (null = root) → ordered child ids. */
type Outline = Map<string | null, string[]>;

function toOutline(lines: readonly StructureLine[]): Outline {
  const outline: Outline = new Map();
  for (const [parent, group] of groupChildren(lines)) {
    outline.set(
      parent,
      group.map((l) => l.id),
    );
  }
  return outline;
}

function parentIn(outline: Outline, lineId: string): string | null | undefined {
  for (const [parent, ids] of outline) if (ids.includes(lineId)) return parent;
  return undefined;
}

function depthIn(outline: Outline, lineId: string): number {
  let depth = 1;
  let current = parentIn(outline, lineId);
  const seen = new Set<string>();
  while (current !== null && current !== undefined && !seen.has(current)) {
    seen.add(current);
    depth += 1;
    current = parentIn(outline, current);
  }
  return depth;
}

/** Levels in the subtree rooted at lineId, counting lineId itself (a leaf is 1). */
function subtreeHeight(outline: Outline, lineId: string, seen = new Set<string>()): number {
  if (seen.has(lineId)) return 0;
  seen.add(lineId);
  let tallest = 0;
  for (const child of outline.get(lineId) ?? []) {
    tallest = Math.max(tallest, subtreeHeight(outline, child, seen));
  }
  return 1 + tallest;
}

/** Full structure, positions renumbered 0..n-1 per sibling group, in display order. */
function fromOutline(outline: Outline): LineStructureEntry[] {
  const out: LineStructureEntry[] = [];
  const emitted = new Set<string>();
  const walk = (parent: string | null): void => {
    (outline.get(parent) ?? []).forEach((id, position) => {
      if (emitted.has(id)) return;
      emitted.add(id);
      out.push({ id, parentLineId: parent, position });
      walk(id);
    });
  };
  walk(null);
  // Lines unreachable from a root (a cycle in the input) are still reported, so the
  // result always covers every line.
  for (const [parent, ids] of outline) {
    ids.forEach((id, position) => {
      if (emitted.has(id)) return;
      emitted.add(id);
      out.push({ id, parentLineId: parent, position });
    });
  }
  return out;
}

type Located = { outline: Outline; parent: string | null; siblings: string[]; index: number };

function locate(lines: readonly StructureLine[], lineId: string): Located | null {
  const outline = toOutline(lines);
  const parent = parentIn(outline, lineId);
  if (parent === undefined) return null;
  const siblings = outline.get(parent);
  if (siblings === undefined) return null;
  return { outline, parent, siblings, index: siblings.indexOf(lineId) };
}

function swap(siblings: string[], a: number, b: number): void {
  const first = siblings[a];
  const second = siblings[b];
  if (first === undefined || second === undefined) return;
  siblings[a] = second;
  siblings[b] = first;
}

export function moveUp(
  lines: readonly StructureLine[],
  lineId: string,
): LineStructureEntry[] | null {
  const found = locate(lines, lineId);
  if (found === null || found.index <= 0) return null;
  swap(found.siblings, found.index, found.index - 1);
  return fromOutline(found.outline);
}

export function moveDown(
  lines: readonly StructureLine[],
  lineId: string,
): LineStructureEntry[] | null {
  const found = locate(lines, lineId);
  if (found === null || found.index >= found.siblings.length - 1) return null;
  swap(found.siblings, found.index, found.index + 1);
  return fromOutline(found.outline);
}

/** Become the last child of the previous sibling. Refused if that would exceed depth 3. */
export function indent(
  lines: readonly StructureLine[],
  lineId: string,
): LineStructureEntry[] | null {
  const found = locate(lines, lineId);
  if (found === null || found.index <= 0) return null;
  const newParent = found.siblings[found.index - 1];
  if (newParent === undefined) return null;
  const { outline } = found;
  const deepest = depthIn(outline, newParent) + subtreeHeight(outline, lineId);
  if (deepest > MAX_LINE_DEPTH) return null;
  found.siblings.splice(found.index, 1);
  const target = outline.get(newParent);
  if (target === undefined) outline.set(newParent, [lineId]);
  else target.push(lineId);
  return fromOutline(outline);
}

/** Become the sibling immediately after its parent. Refused for a root. */
export function outdent(
  lines: readonly StructureLine[],
  lineId: string,
): LineStructureEntry[] | null {
  const found = locate(lines, lineId);
  if (found === null || found.parent === null) return null;
  const { outline, parent } = found;
  const grandparent = parentIn(outline, parent);
  if (grandparent === undefined) return null;
  const uncles = outline.get(grandparent);
  if (uncles === undefined) return null;
  found.siblings.splice(found.index, 1);
  uncles.splice(uncles.indexOf(parent) + 1, 0, lineId);
  return fromOutline(outline);
}

// ── Validation ───────────────────────────────────────────────────────────────

export type StructureValidation = { ok: true } | { ok: false; reason: string };

export function validateStructure(
  existingIds: readonly string[],
  entries: readonly LineStructureEntry[],
): StructureValidation {
  const existing = new Set(existingIds);
  const byId = new Map<string, LineStructureEntry>();
  for (const entry of entries) {
    if (byId.has(entry.id)) return { ok: false, reason: `duplicate line id ${entry.id}` };
    if (!existing.has(entry.id)) return { ok: false, reason: `unknown line id ${entry.id}` };
    byId.set(entry.id, entry);
  }
  for (const id of existing) {
    if (!byId.has(id)) return { ok: false, reason: `missing line id ${id}` };
  }

  for (const entry of entries) {
    if (entry.parentLineId !== null && !byId.has(entry.parentLineId)) {
      return { ok: false, reason: `line ${entry.id} has unknown parent ${entry.parentLineId}` };
    }
  }

  for (const entry of entries) {
    let depth = 1;
    let parent = entry.parentLineId;
    const seen = new Set<string>([entry.id]);
    while (parent !== null) {
      if (seen.has(parent)) return { ok: false, reason: `cycle at line ${entry.id}` };
      seen.add(parent);
      depth += 1;
      parent = byId.get(parent)?.parentLineId ?? null;
    }
    if (depth > MAX_LINE_DEPTH) {
      return {
        ok: false,
        reason: `line ${entry.id} is at depth ${depth}; max is ${MAX_LINE_DEPTH}`,
      };
    }
  }

  const positions = new Map<string | null, Set<number>>();
  for (const entry of entries) {
    if (!Number.isInteger(entry.position) || entry.position < 0) {
      return { ok: false, reason: `line ${entry.id} has invalid position ${entry.position}` };
    }
    const taken = positions.get(entry.parentLineId) ?? new Set<number>();
    if (taken.has(entry.position)) {
      return {
        ok: false,
        reason: `duplicate position ${entry.position} under ${entry.parentLineId ?? "root"}`,
      };
    }
    taken.add(entry.position);
    positions.set(entry.parentLineId, taken);
  }

  return { ok: true };
}

// ── Legacy synthesis ─────────────────────────────────────────────────────────

/** Names shared with the SQL backfill migration — keep identical. */
export const WHOLE_PLAN_LINE_NAME = "Whole-plan estimate";
export const UNASSIGNED_LINE_NAME = "Not assigned to a Workstream";

export type LegacyWorkstreamEstimate = {
  id: string;
  name: string;
  estimatedHours: string | null;
  sortOrder: number | string;
};

export type SynthesizedRootLine = {
  name: string;
  hours: string;
  workstreamId: string | null;
  position: number;
};

/**
 * Root lines that reproduce a plan's pre-outline estimate: one tagged line per
 * Workstream with an estimate (in sortOrder), then either the whole-plan figure (when no
 * Workstream carried one) or a positive remainder as an untagged line, placed last.
 */
export function linesFromLegacyTotals(
  planEstimate: string | null,
  workstreams: readonly LegacyWorkstreamEstimate[],
): SynthesizedRootLine[] {
  const estimated = workstreams
    .flatMap((ws) =>
      ws.estimatedHours === null
        ? []
        : [{ ws, centi: parseHoursCenti(ws.estimatedHours), order: Number(ws.sortOrder) }],
    )
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      if (a.ws.id < b.ws.id) return -1;
      if (a.ws.id > b.ws.id) return 1;
      return 0;
    });

  const out: SynthesizedRootLine[] = estimated.map(({ ws, centi }, position) => ({
    name: ws.name,
    hours: formatHoursCenti(centi),
    workstreamId: ws.id,
    position,
  }));

  if (planEstimate === null) return out;
  const planCenti = parseHoursCenti(planEstimate);

  if (estimated.length === 0) {
    return [
      {
        name: WHOLE_PLAN_LINE_NAME,
        hours: formatHoursCenti(planCenti),
        workstreamId: null,
        position: 0,
      },
    ];
  }

  const remainder = planCenti - estimated.reduce((sum, e) => sum + e.centi, 0);
  if (remainder > 0) {
    out.push({
      name: UNASSIGNED_LINE_NAME,
      hours: formatHoursCenti(remainder),
      workstreamId: null,
      position: out.length,
    });
  }
  return out;
}
